import { useState, useEffect } from "react";

interface UseResponsiveDrawerResult {
  isMobile: boolean;
  shouldUseDrawer: boolean;
}

/**
 * Hook to manage responsive drawer behavior
 * Detects mobile viewport and determines if drawer should be used
 *
 * @param forceDrawer - Force drawer mode even on desktop
 * @param forceRegular - Force regular content mode even on mobile
 * @returns Object with isMobile and shouldUseDrawer flags
 */
export function useResponsiveDrawer(
  forceDrawer?: boolean,
  forceRegular?: boolean
): UseResponsiveDrawerResult {
  const [isMobile, setIsMobile] = useState(false);
  const [shouldUseDrawer, setShouldUseDrawer] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);

      // Determine if we should use drawer
      if (forceDrawer) {
        setShouldUseDrawer(true);
      } else if (forceRegular) {
        setShouldUseDrawer(false);
      } else {
        // Default behavior: drawer on mobile, regular on desktop
        setShouldUseDrawer(mobile);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [forceDrawer, forceRegular]);

  return { isMobile, shouldUseDrawer };
}
